import { toast } from 'react-toastify';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import md5 from 'md5';
import header from '../../../assets/icm-header.jpg';
import {
  getBase64PDFImage,
  scaleStyles,
  baseStyles,
} from '../../../util/utils';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const stripHtml = html => {
  if (!html || typeof html !== 'string') return '';
  // Substitui <p> e </p> por quebras de linha para preservar parágrafos
  let clean = html.replace(/<\/?p[^>]*>/gi, '\n');
  const tmp = document.createElement('div');
  tmp.innerHTML = clean;
  return tmp.textContent || tmp.innerText || '';
};

// Função utilitária para converter BMP para PNG base64
export async function convertBmpToPng(base64Bmp) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = base64Bmp;
  });
}

export default async function generateEcgPDF(ecg, currentImageBase64) {
  const styles = scaleStyles(baseStyles, 1.0);
  const headerImage = await getBase64PDFImage(header);

  const pngBase64 = await convertBmpToPng(currentImageBase64);

  // Preserva parágrafos da descrição
  const descricaoTexto = stripHtml(ecg.OBSERVACAO || '')
    .split(/\n/)
    .map(s => s.trim())
    .filter(Boolean);

  const docDefinition = {
    pageSize: 'A4',
    pageMargins: headerImage ? [40, 130, 40, 30] : [40, 60, 40, 30],
    footer: (currentPage, pageCount) => ({
      text: `Página ${currentPage} de ${pageCount}`,
      alignment: 'center',
      style: 'footer',
    }),
    header: {
      image: headerImage,
      width: 500,
      alignment: 'center',
      margin: [0, 10, 0, 0],
    },
    content: [
      {
        text: 'ELETROCARDIOGRAMA',
        style: 'title',
        alignment: 'center',
        margin: [0, 20, 0, 50],
      },
      // --- Cabeçalho com retângulo preto ---
      {
        table: {
          widths: ['*'], // ocupa toda a largura
          body: [
            [
              {
                stack: [
                  {
                    text: `Nome do Paciente: ${ecg.CODPAC} - ${ecg.NOMEPAC}`,
                    bold: true,
                  },
                  {
                    columns: [
                      { text: `Data Nasc: ${ecg.dataNasc}`, width: '50%' },
                      { text: `Sexo: ${ecg.SEXOPAC}`, width: '20%' },
                      { text: `Idade: ${ecg.idadePAC} anos`, width: '30%' },
                    ],
                  },
                  {
                    columns: [
                      { text: `Convênio: ${ecg.SIGLACONV}`, width: '50%' },
                      { text: `Código: ${ecg.CODECG}`, width: '20%' },
                      {
                        text: `Data Laudo: ${ecg.dataEcg}`,
                        width: '30%',
                      },
                    ],
                  },
                  {
                    text: `Médico que Realizou: Dr. ${ecg.Realizou}`,
                  },
                  {
                    text: `Médico que Solicitou: Dr. ${ecg.Solicitou}`,
                  },
                ],
                margin: [10, 0, 10, 0],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => 'black',
          vLineColor: () => 'black',
        },
        style: 'content',
      },
      {
        text: 'DESCRIÇÃO DO LAUDO:',
        style: 'content',
        bold: true,
        margin: [0, 30, 0, 0],
      },
      // Descrição com parágrafos preservados
      {
        stack: (descricaoTexto.length > 0 ? descricaoTexto : [' ']).map(
          paragrafo => ({
            text: '- ' + paragrafo,
            style: 'content',
            margin: [0, 0, 0, 8],
          })
        ),
        margin: [0, 15, 0, 200],
      },
      // Tabela de assinatura próxima ao rodapé
      {
        table: {
          widths: ['*'],
          body: [
            [
              {
                stack: [
                  {
                    text: 'Conferido e assinado eletronicamente por:',
                    italics: true,
                    alignment: 'center',
                  },
                  {
                    text: `Dr. ${ecg.Realizou || ''}`,
                    bold: true,
                    alignment: 'center',
                  },
                  {
                    text: `CRM: ${ecg.CRMMED || ''} - RQE: ${ecg.RQE || ''}`,
                    bold: true,
                    style: 'small',
                    alignment: 'center',
                  },
                  {
                    text: `Autenticação: ${md5(JSON.stringify(ecg || ''))}`,
                    italics: true,
                    style: 'smaller',
                    alignment: 'center',
                  },
                  {
                    text: [
                      { text: 'Emitido em: ', bold: true },
                      `${new Date().toLocaleString('pt-BR')}\n`,
                    ],
                    style: 'smaller',
                    color: '#444',
                    alignment: 'center',
                  },
                ],
                style: 'content',
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: '#999',
          vLineColor: '#999',
          paddingLeft: () => 5,
          paddingRight: () => 5,
          paddingTop: () => 5,
          paddingBottom: () => 5,
        },
        margin: [0, 60, 0, 0],
      },
      // Imagem do ECG
      {
        image: pngBase64,
        width: 500,
        alignment: 'center',
      },
    ],
    styles,
  };

  if (!ecg.CODPAC || !ecg.NOMEPAC) {
    toast.error('Dados do paciente incompletos!');
    return undefined;
  }

  return pdfMake.createPdf(docDefinition);
}
